import { useCallback, useEffect, useSyncExternalStore } from "react";
import { useSelector } from "react-redux";
import { isVirtualWorldTestHost } from "../helper/virtualWorldHost";
import vw from "../api/virtualWorld.api";

/** Ein Store für alle Manager-/Career-Seiten — sonst bleibt Layout/Guide nach Aktionen stale. */
let store = {
  career: null,
  manager: null,
  loading: true,
  error: "",
  userId: null,
  loaded: false,
};
const listeners = new Set();
let inflight = null;

function emit() {
  listeners.forEach((fn) => fn());
}

function getSnapshot() {
  return store;
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function patch(partial) {
  store = { ...store, ...partial };
  emit();
}

export default function useVirtualWorld() {
  const user = useSelector((s) => s.auth.user);
  const userId = user?._id;
  const enabled = isVirtualWorldTestHost();
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const setCareer = useCallback((career) => {
    patch({ career: typeof career === "function" ? career(store.career) : career });
  }, []);

  const setManager = useCallback((manager) => {
    patch({ manager: typeof manager === "function" ? manager(store.manager) : manager });
  }, []);

  const refresh = useCallback(async () => {
    if (!userId || !enabled) {
      patch({ loading: false, career: null, manager: null, loaded: false, userId: null });
      return null;
    }
    if (inflight && store.userId === userId) return inflight;

    const sameUser = store.userId === userId;
    const showSpinner = !store.loaded || !sameUser;
    if (showSpinner) patch({ loading: true, error: "" });
    else patch({ error: "" });

    inflight = (async () => {
      // Getrennt laden: ein Career-Fehler darf den Manager-Club nicht „wegwischen“.
      const [cRes, mRes] = await Promise.allSettled([vw.career(userId), vw.manager(userId)]);
      const careerOk = cRes.status === "fulfilled";
      const managerOk = mRes.status === "fulfilled";
      const careerData = careerOk ? cRes.value?.data : sameUser ? store.career : null;
      const managerData = managerOk ? mRes.value?.data : sameUser ? store.manager : null;
      const fail =
        (!careerOk && cRes.reason) ||
        (!managerOk && mRes.reason) ||
        null;
      const msg = fail
        ? fail?.data?.message ||
          fail?.message ||
          (fail?.status ? `Fehler (${fail.status})` : "Server nicht erreichbar")
        : "";

      patch({
        career: careerData,
        manager: managerData,
        loading: false,
        loaded: Boolean(managerOk || careerOk || (sameUser && store.loaded)),
        userId,
        error: managerOk ? "" : msg,
      });
      return managerData;
    })().finally(() => {
      inflight = null;
    });

    return inflight;
  }, [userId, enabled]);

  useEffect(() => {
    if (!enabled) {
      patch({ loading: false, career: null, manager: null, loaded: false });
      return undefined;
    }
    // User-Wechsel: erst spinnern, alten Club nicht sofort löschen (verhindert
    // „Bitte Club gründen“ bei kurzem API-Aussetzer / Backend-Restart).
    if (userId && userId !== store.userId) {
      patch({
        loading: true,
        loaded: false,
        error: "",
        userId,
      });
    }
    refresh();
    return undefined;
  }, [refresh, userId, enabled]);

  return {
    user,
    userId,
    career: snap.career,
    manager: snap.manager,
    loading: snap.loading,
    error: snap.error,
    refresh,
    setCareer,
    setManager,
  };
}
